/*
 * The encrypted offline store (ToR 1.0(xi), 1.0(xii), 4.1.5): IndexedDB through idb, every record an
 * AES-GCM blob under the data key. Packs (the compiled PDF with its page map, the contents, the
 * papers where the policy allows, the notes), the wrapped data key, and the outbox of what was done
 * offline. Nothing here is readable without the device key the server issues (crypto.ts).
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { decryptBytes, decryptJson, encryptBytes, encryptJson } from './crypto';

export interface StoredPackMeta {
  distributionId: string;
  boardPackId: string;
  title: string;
  versionNo: number;
  periodName: string | null;
  syncedAt: string;
  expiresAt: string;
  pages: Record<string, number>;
  papers: { documentId: string; versionNo: number; title: string; fileName: string; stored: boolean }[];
}

export interface OutboxItem {
  id: string;
  kind: 'annotation' | 'readEvent' | 'readingPosition' | 'acknowledge';
  distributionId: string;
  body: unknown;
  queuedAt: string;
  attempts: number;
}

interface Schema extends DBSchema {
  keys: { key: string; value: { id: string; wrapped: Uint8Array; deviceId: string; createdAt: string } };
  packs: { key: string; value: { distributionId: string; meta: Uint8Array; updatedAt: string } };
  blobs: { key: string; value: { key: string; distributionId: string; bytes: Uint8Array } };
  outbox: { key: string; value: { id: string; sealed: Uint8Array; queuedAt: string } };
}

const DB_NAME = 'annova-offline';
const DB_VERSION = 1;

let db: Promise<IDBPDatabase<Schema>> | null = null;

function open(): Promise<IDBPDatabase<Schema>> {
  db ??= openDB<Schema>(DB_NAME, DB_VERSION, {
    upgrade(d) {
      d.createObjectStore('keys', { keyPath: 'id' });
      d.createObjectStore('packs', { keyPath: 'distributionId' });
      d.createObjectStore('blobs', { keyPath: 'key' });
      d.createObjectStore('outbox', { keyPath: 'id' });
    },
  });
  return db;
}

/** Forgets the whole store: the remote wipe, a sign-out, an expiry without contact. */
export async function purgeStore(): Promise<void> {
  if (db) { (await db).close(); db = null; }
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

export async function getWrappedKey(): Promise<{ wrapped: Uint8Array; deviceId: string } | null> {
  const row = await (await open()).get('keys', 'data');
  return row ? { wrapped: row.wrapped, deviceId: row.deviceId } : null;
}

export async function putWrappedKey(wrapped: Uint8Array, deviceId: string): Promise<void> {
  await (await open()).put('keys', { id: 'data', wrapped, deviceId, createdAt: new Date().toISOString() });
}

export async function listPacks(key: CryptoKey): Promise<StoredPackMeta[]> {
  const rows = await (await open()).getAll('packs');
  const out: StoredPackMeta[] = [];
  for (const r of rows) {
    try { out.push(await decryptJson<StoredPackMeta>(key, r.meta)); } catch { /* a record sealed under another key: unreadable, left for the purge */ }
  }
  return out.sort((a, b) => b.syncedAt.localeCompare(a.syncedAt));
}

export async function getPackMeta(key: CryptoKey, distributionId: string): Promise<StoredPackMeta | null> {
  const row = await (await open()).get('packs', distributionId);
  if (!row) return null;
  try { return await decryptJson<StoredPackMeta>(key, row.meta); } catch { return null; }
}

export async function putPackMeta(key: CryptoKey, meta: StoredPackMeta): Promise<void> {
  await (await open()).put('packs', { distributionId: meta.distributionId, meta: await encryptJson(key, meta), updatedAt: new Date().toISOString() });
}

export async function putBlob(key: CryptoKey, distributionId: string, name: string, bytes: Uint8Array): Promise<void> {
  await (await open()).put('blobs', { key: `${distributionId}:${name}`, distributionId, bytes: await encryptBytes(key, bytes) });
}

export async function getBlob(key: CryptoKey, distributionId: string, name: string): Promise<Uint8Array | null> {
  const row = await (await open()).get('blobs', `${distributionId}:${name}`);
  if (!row) return null;
  try { return await decryptBytes(key, row.bytes); } catch { return null; }
}

export async function putJson(key: CryptoKey, distributionId: string, name: string, value: unknown): Promise<void> {
  await (await open()).put('blobs', { key: `${distributionId}:${name}`, distributionId, bytes: await encryptJson(key, value) });
}

export async function getJson<T>(key: CryptoKey, distributionId: string, name: string): Promise<T | null> {
  const row = await (await open()).get('blobs', `${distributionId}:${name}`);
  if (!row) return null;
  try { return await decryptJson<T>(key, row.bytes); } catch { return null; }
}

export async function removePack(distributionId: string): Promise<void> {
  const d = await open();
  await d.delete('packs', distributionId);
  const tx = d.transaction('blobs', 'readwrite');
  for (const k of await tx.store.getAllKeys()) if (k.startsWith(`${distributionId}:`)) await tx.store.delete(k);
  await tx.done;
}

export async function enqueue(key: CryptoKey, item: OutboxItem): Promise<void> {
  await (await open()).put('outbox', { id: item.id, sealed: await encryptJson(key, item), queuedAt: item.queuedAt });
}

export async function listOutbox(key: CryptoKey): Promise<OutboxItem[]> {
  const rows = await (await open()).getAll('outbox');
  const out: OutboxItem[] = [];
  for (const r of rows) { try { out.push(await decryptJson<OutboxItem>(key, r.sealed)); } catch { /* unreadable */ } }
  return out.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

export async function dequeue(id: string): Promise<void> {
  await (await open()).delete('outbox', id);
}

export async function outboxCount(): Promise<number> {
  return (await open()).count('outbox');
}

/** Removes one sealed item (a payslip kept for offline reading, say) without touching the rest of its group. */
export async function removeBlob(distributionId: string, name: string): Promise<void> {
  await (await open()).delete('blobs', `${distributionId}:${name}`);
}
